import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { FrankencoinMarketsRaw } from './types'

/**
 * Synthesized per-market lender key, e.g.
 * `FRANKENCOIN_1_5F2C10F7…` — one per ORIGINAL position. The chain id is part
 * of the key (Fluid/River convention) even though Frankencoin is
 * Ethereum-only today, so an L2 hub would not collide.
 */
export function frankencoinLenderKey(
  lender: string,
  chainId: string | number,
  position: string,
): string {
  return `${lender}_${chainId}_${position.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, chainId, position }` from a per-market key. */
export function frankencoinKeyParts(
  key: string,
): { lender: string; chainId: string; position: string } | undefined {
  if (!key.startsWith('FRANKENCOIN_')) return undefined
  const suffix = key.slice('FRANKENCOIN_'.length)
  const m = suffix.match(/^(\d+)_([0-9A-Fa-f]{40})$/)
  if (!m) return undefined
  return {
    lender: 'FRANKENCOIN',
    chainId: m[1],
    position: `0x${m[2].toLowerCase()}`,
  }
}

const PPM = 1e6

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map one Frankencoin deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed `FRANKENCOIN_<chainId>_<ORIGINAL>`.
 *
 * Three modelling decisions worth knowing (all from FRANKENCOIN.md):
 *
 *  - **The liquidation price is owner-DECLARED, not an oracle.** `price` is
 *    36-dec scaled against the collateral's decimals and is policed by a
 *    Dutch-auction challenge game. We surface it as the LIQUIDATION price
 *    (it defines `collateralFactor = 1`, i.e. minting is allowed up to
 *    `coll × price`) but value collateral with OUR price feeds. The
 *    divergence between the two is the risk signal, and it is carried
 *    verbatim in `params.market.frankencoin.declaredPrice` so a consumer can
 *    compute it. Never present the resulting health as a protocol invariant.
 *  - **Debt ≠ proceeds.** `minted` is FACE debt including the withheld
 *    `reserveContribution` (10–40 %) plus the upfront pro-rata fee. Both
 *    ppm figures ride along in the descriptor so a quote layer can convert.
 *  - **Positions expire.** `expiration` is surfaced and an expired market is
 *    reported halted (its collateral is subject to forced sale).
 *
 * Per market: a COLLATERAL entry (the original's own collateral) and a LOAN
 * entry (ZCHF). There is no protocol supply side — ZCHF is minted, and the
 * earn leg is the separate savings module carried by the savings provider —
 * so `totalDeposits` on the loan row is 0.
 */
export function convertFrankencoinMarketsToResponse(
  raw: FrankencoinMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  const chainData = raw?.chainData
  if (!cfg || !chainData) return out

  const debtAddr = cfg.zchf.toLowerCase()
  const debtDecimals = cfg.debtDecimals ?? 18
  const debtToken = tokens[debtAddr]
  const debtSymbol = debtToken?.symbol ?? 'ZCHF'
  const debtPriceKey =
    toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)
  // ZCHF is CHF-denominated: without a CHF/USD price in the shared map we
  // must NOT silently assume a 1 USD peg (that would overstate every USD
  // figure by ~23 %). 0 propagates as "unknown" rather than "one dollar".
  const debtPrice = prices[debtPriceKey] ?? 0

  const nowSec = Math.floor(Date.now() / 1000)

  for (const m of raw.markets ?? []) {
    const market = m.market
    const lenderKey = frankencoinLenderKey(raw.lender, chainId, market.position)
    const collAddr = market.collToken.toLowerCase()
    const collDecimals = market.collDecimals
    const collToken = tokens[collAddr]
    const collSymbol = collToken?.symbol ?? market.collSymbol ?? 'COLL'
    const marketName = market.name ?? `${debtSymbol} / ${collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    const collPrice = prices[collPriceKey] ?? 0

    // Declared liquidation price → ZCHF per whole collateral unit.
    const declaredPrice =
      m.price !== null
        ? Number(m.price) / 10 ** (36 - collDecimals)
        : Number(market.price) / 10 ** (36 - collDecimals)

    const totalColl =
      m.collateralBalance !== null
        ? Number(m.collateralBalance) / 10 ** collDecimals
        : 0
    const totalDebt =
      m.minted !== null ? Number(m.minted) / 10 ** debtDecimals : 0
    const borrowLiquidity =
      m.availableForClones !== null
        ? Number(m.availableForClones) / 10 ** debtDecimals
        : 0

    const expiration =
      m.expiration !== null ? Number(m.expiration) : Number(market.expiration)
    const expired = expiration > 0 && expiration <= nowSec
    const challenged = (m.challengedAmount ?? 0n) > 0n
    // A closed position can never mint again (it is terminal — dropping
    // below `minimumCollateral` closes one permanently), so it is halted
    // regardless of expiry. Read live: the metadata roster can lag.
    const closed = m.isClosed === true
    const halted = expired || closed

    // Ongoing borrow APR (ppm → percent). The UPFRONT fee is a separate,
    // one-off cost and belongs in `originationFee`, not in the APR — same
    // split as River's decaying mint fee.
    const borrowApr =
      m.annualInterestPPM !== null
        ? (Number(m.annualInterestPPM) / PPM) * 100
        : 0
    const upfrontFeePct =
      m.currentFeePPM !== null ? (Number(m.currentFeePPM) / PPM) * 100 : 0
    const reserveContributionPpm =
      m.reserveContribution !== null
        ? Number(m.reserveContribution)
        : Number(market.reserveContribution)

    // Minting is allowed up to `coll × declaredPrice` ZCHF, so relative to
    // the DECLARED price the collateral factor is 1. Expressed against our
    // market price it is `declaredPrice / collPrice` — the meaningful LTV,
    // and the number that reveals an optimistic mark. Frankencoin's own UI
    // shows exactly this figure (a full-capacity mint against a 45,000 ZCHF
    // declared price at a ~51,850 ZCHF market reads 86.79 %).
    const ltvVsMarket =
      collPrice > 0 ? Math.min(declaredPrice / collPrice, 1) : 0

    // THE LIQUIDATION SIGNAL — deliberately UNCLAMPED, unlike the collateral
    // factor above. A challenge succeeds only when nobody averts it, and
    // averting (buying the challenger's collateral at the DECLARED price) is
    // profitable exactly while market > declared. So the position becomes
    // challengeable at `declaredVsMarket >= 1`, independently of how much
    // debt it carries: `MintingHub.challenge()` has NO health gate, and
    // repaying does NOT reduce challenge risk (only lowering the declared
    // price via `adjustPrice` does). Never derive liquidation distance from
    // the health factor here — see the note in FRANKENCOIN.md.
    const declaredVsMarket =
      collPrice > 0 ? declaredPrice / collPrice : undefined

    // @ts-ignore — built incrementally, matches the River/USDD converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: market.position.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: totalColl,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: totalColl,
      borrowLiquidity: 0,
      totalLiquidityUSD: totalColl * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: totalColl * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltvVsMarket,
          collateralFactor: ltvVsMarket,
          borrowFactor: 1,
          // Liquidation is a Dutch auction against the declared price, not a
          // fixed penalty; the challenger reward (2 %) is the closest
          // bounded cost and is carried in the descriptor.
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !halted,
      hasStable: false,
      isActive: !halted,
      isFrozen: halted,
    } as any

    const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: debtSymbol,
      poolId: cfg.mintingHub.toLowerCase(),
      underlying: debtAddr,
      asset: currencyFor(debtAddr, debtDecimals, debtSymbol, tokens),
      totalDeposits: 0,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * debtPrice,
      borrowLiquidityUSD: borrowLiquidity * debtPrice,
      totalDepositsUSD: 0,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * debtPrice,
      utilization: 0,
      // The earn side is the savings module (savings provider), not a
      // lending deposit.
      depositRate: 0,
      variableBorrowRate: borrowApr,
      stableBorrowRate: 0,
      // Per-position fixed rate (hub lead rate + risk premium) — no
      // utilization curve anywhere in the protocol.
      rateModel: 'protocolSet',
      // One-off, pro-rata to expiry, charged at mint out of the proceeds.
      originationFee: upfrontFeePct,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: debtDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: true,
          debtDisabled: halted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !halted && !challenged,
      depositsEnabled: false,
      hasStable: false,
      variableBorrowDisabled: halted || challenged,
      isActive: !halted,
      isFrozen: halted,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals: debtDecimals,
        collateralDecimals: collDecimals,
        // The original position doubles as the market id.
        id: market.position.toLowerCase(),
        lltv: m.price !== null ? m.price.toString() : market.price,
        // There is no oracle contract — deliberately zero, with the
        // declared price carried below.
        oracle: zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        // --- Frankencoin descriptor (consumed by the calldata builders,
        // worker-api resolvers and any quote layer) ---
        frankencoin: {
          position: market.position,
          /** Owner-DECLARED liquidation price (raw, 36-dec scaled). */
          declaredPrice: m.price !== null ? m.price.toString() : market.price,
          /** Same, as ZCHF per whole collateral unit. */
          declaredPriceHuman: declaredPrice,
          /** `declaredPrice / marketPrice`, UNCLAMPED — the liquidation
           *  signal. `>= 1` means the collateral can no longer be sold at
           *  the declared price, so a challenge would succeed. `undefined`
           *  when we have no market price for the collateral. This is NOT
           *  the health factor: our HF is `mintingCapacity / minted` (the
           *  protocol's own `minted <= coll * price` invariant), which
           *  answers "can I borrow more", not "am I liquidatable". */
          declaredVsMarket,
          /** True when the declared price has been reached or passed and a
           *  challenge would therefore clear. Undefined without a market
           *  price. */
          challengeable:
            declaredVsMarket === undefined ? undefined : declaredVsMarket >= 1,
          /** ppm withheld into the FPS equity reserve at mint. */
          reserveContributionPPM: String(reserveContributionPpm),
          /** ppm one-off upfront interest for minting now. */
          upfrontFeePPM:
            m.currentFeePPM !== null ? m.currentFeePPM.toString() : undefined,
          annualInterestPPM:
            m.annualInterestPPM !== null
              ? m.annualInterestPPM.toString()
              : undefined,
          riskPremiumPPM: market.riskPremiumPPM,
          minimumCollateral: market.minimumCollateral,
          expiration: String(expiration),
          expired,
          closed,
          challengePeriod: market.challengePeriod,
          challengedAmount:
            m.challengedAmount !== null
              ? m.challengedAmount.toString()
              : undefined,
          isChallenged: challenged,
          /** ppm reward paid to a successful challenger (hub constant). */
          challengerRewardPPM: '20000',
          version: market.version ?? 2,
          addresses: {
            mintingHub: cfg.mintingHub,
            zchf: cfg.zchf,
            equity: cfg.equity,
            roller: cfg.roller,
            chfUsdFeed: cfg.chfUsdFeed,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
